import React, { useEffect, useState } from "react";
import { Loader2, History, ExternalLink } from "lucide-react";
import { api, brl, fmtDate } from "@/lib/api";

const TIPO = { mesmo_orgao: "Mesmo órgão já comprou", produto_na_uf: "Produto recorrente na UF" };

function Resumo({ r }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
      {[["Compras anteriores do órgão", r.compras_orgao ?? 0], ["Itens semelhantes (órgão / UF)", `${r.itens_orgao ?? 0} / ${r.itens_uf ?? 0}`],
        [`Desconto médio (${r.base_ref || "—"})`, r.desconto_medio_pct != null ? `${r.desconto_medio_pct}%` : "—"], ["Último resultado", fmtDate(r.ultima_data)]].map(([l, v]) => (
        <div key={l} className="rounded-lg bg-white p-2"><div className="text-[10px] text-slate-400">{l}</div><div className="font-bold text-slate-800">{v}</div></div>
      ))}
    </div>
  );
}

export const BenchmarkHistorico = ({ oid }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/benchmark/opportunity/${oid}`).then((r) => setD(r.data)).catch(() => setD({ erro: true }));
  }, [oid]);
  if (!d) return <div className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Consultando histórico (Benchmark)…</div>;
  if (d.erro) return null;
  const r = d.recorrencia || {};
  return (
    <div data-testid="drawer-benchmark-panel" className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-sm font-bold text-violet-900"><History className="h-4 w-4" /> Histórico · PRO INVICTA Benchmark</div>
        {r.is ? <span data-testid="drawer-recorrente-badge" className="rounded-full bg-violet-600 px-2 py-0.5 text-[11px] font-bold text-white">♻ Oportunidade recorrente</span>
          : <span className="text-[11px] text-slate-400">Sem recorrência identificada</span>}
      </div>
      {(r.tipos || []).length > 0 && <div className="mb-2 flex flex-wrap gap-1">{r.tipos.map((t) => <span key={t} className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">{TIPO[t] || t}</span>)}</div>}
      <Resumo r={r} />
      {(r.vencedores || []).length > 0 && (
        <div className="mt-2 text-xs text-slate-600" data-testid="drawer-bench-vencedores"><b>Quem venceu antes:</b> {r.vencedores.map((v) => `${v.nome} (${v.vitorias})`).join(" · ")}</div>
      )}
      {(d.referencias || []).length > 0 && (
        <div className="mt-3 space-y-2" data-testid="drawer-bench-refs">
          <div className="text-[11px] font-bold uppercase text-slate-500">Preços praticados em itens semelhantes</div>
          {d.referencias.map((ref, i) => (
            <div key={i} className="rounded-lg border border-slate-100 bg-white p-2 text-xs">
              <div className="mb-1 font-semibold text-slate-700">{ref.numero_item ? `Item ${ref.numero_item} · ` : ""}{ref.descricao}{ref.valor_unitario_estimado ? <span className="font-normal text-slate-400"> (estimado agora {brl(ref.valor_unitario_estimado)})</span> : null}</div>
              {ref.historico.map((h) => (
                <div key={h.id} className="flex justify-between gap-2 border-t border-slate-50 py-0.5 text-[11px]">
                  <span className="line-clamp-1 text-slate-500">{fmtDate(h.data_resultado)} · {h.orgao_nome} — {h.descricao}</span>
                  <span className="shrink-0 font-semibold text-slate-800">{brl(h.valor_unitario_homologado)} · {h.fornecedor_nome}{h.marca ? ` · ${h.marca}` : ""}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {(d.compras_orgao || []).length > 0 && (
        <div className="mt-3 text-xs" data-testid="drawer-bench-compras">
          <div className="mb-1 text-[11px] font-bold uppercase text-slate-500">Licitações anteriores deste órgão</div>
          {d.compras_orgao.map((c) => (
            <a key={c.numero_controle} href={c.link} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 py-0.5 text-slate-600 hover:text-sky-700">
              <span className="line-clamp-1">{fmtDate(c.data_publicacao)} · {c.objeto}</span><ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          ))}
        </div>
      )}
      <p className="mt-2 text-[10px] text-slate-400">Fonte: resultados oficiais publicados no PNCP. Apoio à decisão — não substitui a análise do edital.</p>
    </div>
  );
};
